import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Tooltip,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  AccountTree as WorkflowIcon,
  SaveAlt as SaveTemplateIcon,
  FolderOpen as TemplateIcon,
} from '@mui/icons-material';
import workflowService from '../../services/workflowService';
import { ticketsApi, benutzerApi, webhooksApi } from '../../services/api';
import type {
  WorkflowDto,
  WorkflowSchrittDto,
  WorkflowVorlageDto,
  Ticket,
  Benutzer,
  Webhook,
  NewWorkflowFormData,
  NewVorlageFormData,
  EditVorlageFormData,
} from './types';
import { formatDate, getStatusColor } from './utils';

// Import sub-components
import VorlageCard from './VorlageCard';
import WorkflowCard from './WorkflowCard';
import CreateWorkflowDialog from './CreateWorkflowDialog';
import WorkflowDetailsDialog from './WorkflowDetailsDialog';
import AssignTicketDialog from './AssignTicketDialog';
import CreateVorlageDialog from './CreateVorlageDialog';
import ExecuteVorlageDialog from './ExecuteVorlageDialog';
import EditVorlageDialog from './EditVorlageDialog';

const INITIAL_WORKFLOW_FORM: NewWorkflowFormData = {
  name: '',
  kategorie: '',
  beschreibung: '',
  schritte: [{ schrittNummer: 1, titel: '', was: '', wie: '', prioritaet: 'Mittel' }],
};

const INITIAL_VORLAGE_FORM: NewVorlageFormData = {
  name: '',
  kategorie: '',
  beschreibung: '',
  schritte: [{ schrittNummer: 1, titel: '', was: '', wie: '', prioritaet: 'Mittel' }],
};

const Workflows: React.FC = () => {
  // Data states
  const [workflows, setWorkflows] = useState<WorkflowDto[]>([]);
  const [vorlagen, setVorlagen] = useState<WorkflowVorlageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingVorlagen, setLoadingVorlagen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createVorlageDialogOpen, setCreateVorlageDialogOpen] = useState(false);
  const [editVorlageDialogOpen, setEditVorlageDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [assignTicketDialogOpen, setAssignTicketDialogOpen] = useState(false);
  const [executeVorlageDialogOpen, setExecuteVorlageDialogOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowDto | null>(null);
  const [selectedVorlage, setSelectedVorlage] = useState<WorkflowVorlageDto | null>(null);

  // Reference data
  const [availableTickets, setAvailableTickets] = useState<Ticket[]>([]);
  const [availableUsers, setAvailableUsers] = useState<Benutzer[]>([]);
  const [availableWebhooks, setAvailableWebhooks] = useState<Webhook[]>([]);

  // Form states
  const [newWorkflow, setNewWorkflow] = useState<NewWorkflowFormData>(INITIAL_WORKFLOW_FORM);
  const [newVorlage, setNewVorlage] = useState<NewVorlageFormData>(INITIAL_VORLAGE_FORM);
  const [editVorlage, setEditVorlage] = useState<EditVorlageFormData>({
    vorlageID: 0,
    name: '',
    kategorie: '',
    beschreibung: '',
    istAktiv: true,
    schritte: [],
  });
  const [executeVorlageName, setExecuteVorlageName] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);

  // Loading states
  const [creating, setCreating] = useState(false);
  const [creatingVorlage, setCreatingVorlage] = useState(false);
  const [updatingVorlage, setUpdatingVorlage] = useState(false);
  const [executingVorlage, setExecutingVorlage] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [deletingVorlageId, setDeletingVorlageId] = useState<number | null>(null);

  useEffect(() => {
    loadWorkflows();
    loadVorlagen();
    loadUsers();
    loadWebhooks();
  }, []);

  // Data loading functions
  const loadWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await workflowService.getAll();
      setWorkflows(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Fehler beim Laden der Workflows:', err);
      setError(error.response?.data?.message || 'Fehler beim Laden der Workflows');
    } finally {
      setLoading(false);
    }
  };

  const loadVorlagen = async () => {
    try {
      setLoadingVorlagen(true);
      const data = await workflowService.getAllVorlagen();
      setVorlagen(data);
    } catch (err) {
      console.error('Fehler beim Laden der Vorlagen:', err);
    } finally {
      setLoadingVorlagen(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await benutzerApi.getMandantBenutzer();
      setAvailableUsers(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Benutzer:', err);
    }
  };

  const loadWebhooks = async () => {
    try {
      const response = await webhooksApi.getAll();
      setAvailableWebhooks(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Webhooks:', err);
    }
  };

  const loadAvailableTickets = async () => {
    try {
      const response = await ticketsApi.getAll({ take: 100 });
      const ticketsWithoutWorkflow = response.data.filter((t: Ticket) => !t.workflowID);
      setAvailableTickets(ticketsWithoutWorkflow);
    } catch (err) {
      console.error('Fehler beim Laden der Tickets:', err);
    }
  };

  // Tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Create Workflow handlers
  const handleOpenCreateDialog = () => {
    setNewWorkflow({ ...INITIAL_WORKFLOW_FORM, schritte: [{ schrittNummer: 1, titel: '', was: '', wie: '', prioritaet: 'Mittel' }] });
    setCreateDialogOpen(true);
  };

  const handleAddStep = () => {
    setNewWorkflow({
      ...newWorkflow,
      schritte: [
        ...newWorkflow.schritte,
        { schrittNummer: newWorkflow.schritte.length + 1, titel: '', was: '', wie: '', prioritaet: 'Mittel' },
      ],
    });
  };

  const handleRemoveStep = (index: number) => {
    if (newWorkflow.schritte.length <= 1) return;
    const updatedSchritte = newWorkflow.schritte.filter((_, i) => i !== index).map((s, i) => ({
      ...s,
      schrittNummer: i + 1,
    }));
    setNewWorkflow({ ...newWorkflow, schritte: updatedSchritte });
  };

  const handleStepChange = (index: number, field: keyof WorkflowSchrittDto, value: string) => {
    const updatedSchritte = [...newWorkflow.schritte];
    updatedSchritte[index] = { ...updatedSchritte[index], [field]: value };
    setNewWorkflow({ ...newWorkflow, schritte: updatedSchritte });
  };

  const handleCreateWorkflow = async () => {
    if (!newWorkflow.name || !newWorkflow.kategorie || newWorkflow.schritte.some(s => !s.titel || !s.was)) {
      alert('Bitte füllen Sie alle Pflichtfelder aus (Name, Kategorie, Schritt-Titel und Was)');
      return;
    }

    try {
      setCreating(true);
      const firstStepTicket = await ticketsApi.create({
        titel: newWorkflow.schritte[0].titel,
        was: newWorkflow.schritte[0].was,
        wie: newWorkflow.schritte[0].wie || undefined,
        prioritaet: newWorkflow.schritte[0].prioritaet,
        status: 'Offen',
      });

      const result = await workflowService.createWorkflow({
        ticketID: firstStepTicket.data.ticketID,
        kategorie: newWorkflow.kategorie,
        workflowName: newWorkflow.name,
        schritte: newWorkflow.schritte,
      });

      alert(`Workflow "${result.name}" erfolgreich erstellt mit ${result.anzahlSchritte} Schritten!`);
      setCreateDialogOpen(false);
      loadWorkflows();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      console.error('Fehler beim Erstellen:', err);
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setCreating(false);
    }
  };

  // Workflow details handlers
  const handleOpenDetailDialog = async (workflow: WorkflowDto) => {
    try {
      const fullWorkflow = await workflowService.getById(workflow.workflowID);
      setSelectedWorkflow(fullWorkflow);
      setDetailDialogOpen(true);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Fehler beim Laden: ' + (error.response?.data?.message || error.message));
    }
  };

  // Assign ticket handlers
  const handleOpenAssignDialog = (workflow: WorkflowDto) => {
    setSelectedWorkflow(workflow);
    loadAvailableTickets();
    setSelectedTicketId(null);
    setAssignTicketDialogOpen(true);
  };

  const handleAssignTicket = async () => {
    if (!selectedWorkflow || !selectedTicketId) return;

    try {
      setAssigning(true);
      await workflowService.triggerNextStep(selectedTicketId);
      alert('Ticket wurde dem Workflow zugeordnet!');
      setAssignTicketDialogOpen(false);
      setSelectedWorkflow(null);
      setSelectedTicketId(null);
      loadWorkflows();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setAssigning(false);
    }
  };

  // Vorlage handlers
  const handleOpenCreateVorlageDialog = () => {
    setNewVorlage({ ...INITIAL_VORLAGE_FORM, schritte: [{ schrittNummer: 1, titel: '', was: '', wie: '', prioritaet: 'Mittel' }] });
    setCreateVorlageDialogOpen(true);
  };

  const handleVorlageStepChange = (index: number, field: string, value: unknown) => {
    const updatedSchritte = [...newVorlage.schritte];
    updatedSchritte[index] = { ...updatedSchritte[index], [field]: value };
    setNewVorlage({ ...newVorlage, schritte: updatedSchritte });
  };

  const handleAddVorlageStep = () => {
    setNewVorlage({
      ...newVorlage,
      schritte: [
        ...newVorlage.schritte,
        { schrittNummer: newVorlage.schritte.length + 1, titel: '', was: '', wie: '', prioritaet: 'Mittel' },
      ],
    });
  };

  const handleRemoveVorlageStep = (index: number) => {
    if (newVorlage.schritte.length <= 1) return;
    const updatedSchritte = newVorlage.schritte.filter((_, i) => i !== index).map((s, i) => ({
      ...s,
      schrittNummer: i + 1,
    }));
    setNewVorlage({ ...newVorlage, schritte: updatedSchritte });
  };

  const handleCreateVorlage = async () => {
    if (!newVorlage.name || !newVorlage.kategorie || newVorlage.schritte.some(s => !s.titel || !s.was)) {
      alert('Bitte füllen Sie alle Pflichtfelder aus (Name, Kategorie, Schritt-Titel und Was)');
      return;
    }

    try {
      setCreatingVorlage(true);
      await workflowService.createVorlage({
        name: newVorlage.name,
        kategorie: newVorlage.kategorie,
        beschreibung: newVorlage.beschreibung || undefined,
        schritte: newVorlage.schritte,
      });
      alert(`Vorlage "${newVorlage.name}" erfolgreich erstellt!`);
      setCreateVorlageDialogOpen(false);
      loadVorlagen();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      console.error('Fehler beim Erstellen der Vorlage:', err);
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setCreatingVorlage(false);
    }
  };

  // Execute Vorlage handlers
  const handleOpenExecuteDialog = (vorlage: WorkflowVorlageDto) => {
    setSelectedVorlage(vorlage);
    setExecuteVorlageName('');
    setExecuteVorlageDialogOpen(true);
  };

  const handleExecuteVorlage = async () => {
    if (!selectedVorlage) return;

    try {
      setExecutingVorlage(true);
      const result = await workflowService.executeVorlage(
        selectedVorlage.vorlageID,
        executeVorlageName || undefined
      );
      alert(`Workflow "${result.workflowName}" wurde gestartet!\n\nErste Ticket-ID: ${result.ersteTicketID}\nAnzahl Schritte: ${result.anzahlSchritte}`);
      setExecuteVorlageDialogOpen(false);
      setSelectedVorlage(null);
      setExecuteVorlageName('');
      loadWorkflows();
      setCurrentTab(1);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      console.error('Fehler beim Starten der Vorlage:', err);
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setExecutingVorlage(false);
    }
  };

  // Edit Vorlage handlers
  const handleOpenEditVorlageDialog = (vorlage: WorkflowVorlageDto) => {
    setEditVorlage({
      vorlageID: vorlage.vorlageID,
      name: vorlage.name,
      kategorie: vorlage.kategorie,
      beschreibung: vorlage.beschreibung || '',
      istAktiv: vorlage.istAktiv,
      schritte: vorlage.schritte.map(s => ({
        schrittID: s.schrittID,
        schrittNummer: s.schrittNummer,
        titel: s.titel,
        was: s.was,
        wie: s.wie || '',
        prioritaet: s.prioritaet,
        autoZuweisung: s.autoZuweisung,
        autoZuweisungName: s.autoZuweisungName,
        autoWebhookID: s.autoWebhookID,
        autoWebhookName: s.autoWebhookName,
        autoFrist: s.autoFrist,
        autoNotify: s.autoNotify || false,
      })),
    });
    setEditVorlageDialogOpen(true);
  };

  const handleEditVorlageStepChange = (index: number, field: string, value: unknown) => {
    const updatedSchritte = [...editVorlage.schritte];
    updatedSchritte[index] = { ...updatedSchritte[index], [field]: value };
    setEditVorlage({ ...editVorlage, schritte: updatedSchritte });
  };

  const handleAddEditVorlageStep = () => {
    setEditVorlage({
      ...editVorlage,
      schritte: [
        ...editVorlage.schritte,
        { schrittNummer: editVorlage.schritte.length + 1, titel: '', was: '', wie: '', prioritaet: 'Mittel', autoNotify: false },
      ],
    });
  };

  const handleRemoveEditVorlageStep = (index: number) => {
    if (editVorlage.schritte.length <= 1) return;
    const updatedSchritte = editVorlage.schritte.filter((_, i) => i !== index).map((s, i) => ({
      ...s,
      schrittNummer: i + 1,
    }));
    setEditVorlage({ ...editVorlage, schritte: updatedSchritte });
  };

  const handleUpdateVorlage = async () => {
    if (!editVorlage.name || !editVorlage.kategorie || editVorlage.schritte.some(s => !s.titel || !s.was)) {
      alert('Bitte füllen Sie alle Pflichtfelder aus (Name, Kategorie, Schritt-Titel und Was)');
      return;
    }

    try {
      setUpdatingVorlage(true);
      await workflowService.updateVorlage(editVorlage.vorlageID, {
        name: editVorlage.name,
        kategorie: editVorlage.kategorie,
        beschreibung: editVorlage.beschreibung || undefined,
        schritte: editVorlage.schritte,
        istAktiv: editVorlage.istAktiv,
      });
      alert(`Vorlage "${editVorlage.name}" erfolgreich aktualisiert!`);
      setEditVorlageDialogOpen(false);
      loadVorlagen();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      console.error('Fehler beim Aktualisieren der Vorlage:', err);
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setUpdatingVorlage(false);
    }
  };

  // Delete Vorlage
  const handleDeleteVorlage = async (vorlageId: number) => {
    if (!window.confirm('Möchten Sie diese Vorlage wirklich löschen? Dies kann nicht rückgängig gemacht werden.')) {
      return;
    }

    try {
      setDeletingVorlageId(vorlageId);
      await workflowService.deleteVorlage(vorlageId);
      alert('Vorlage wurde gelöscht.');
      loadVorlagen();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } }; message?: string };
      console.error('Fehler beim Löschen der Vorlage:', err);
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeletingVorlageId(null);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Workflows
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Aktualisieren">
              <IconButton onClick={() => { loadWorkflows(); loadVorlagen(); }} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {currentTab === 0 && (
              <Button variant="contained" startIcon={<SaveTemplateIcon />} onClick={handleOpenCreateVorlageDialog}>
                Neue Vorlage
              </Button>
            )}
            {currentTab === 1 && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
                Neuer Workflow
              </Button>
            )}
          </Box>
        </Box>

        <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab icon={<TemplateIcon />} iconPosition="start" label="Workflow-Vorlagen" />
          <Tab icon={<WorkflowIcon />} iconPosition="start" label="Aktive Workflows" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tab 0: Workflow-Vorlagen */}
        {currentTab === 0 && (
          <>
            {loadingVorlagen ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : vorlagen.length === 0 ? (
              <Alert severity="info">
                Keine Workflow-Vorlagen vorhanden. Erstellen Sie eine neue Vorlage, die Benutzer wiederholt abrufen können.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {vorlagen.map((vorlage) => (
                  <Grid item xs={12} md={6} lg={4} key={vorlage.vorlageID}>
                    <VorlageCard
                      vorlage={vorlage}
                      onExecute={handleOpenExecuteDialog}
                      onEdit={handleOpenEditVorlageDialog}
                      onDelete={handleDeleteVorlage}
                      isDeleting={deletingVorlageId === vorlage.vorlageID}
                      formatDate={formatDate}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* Tab 1: Aktive Workflows */}
        {currentTab === 1 && (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : workflows.length === 0 ? (
              <Alert severity="info">
                Keine aktiven Workflows vorhanden. Starten Sie einen Workflow aus einer Vorlage oder erstellen Sie einen neuen Workflow.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {workflows.map((workflow) => (
                  <Grid item xs={12} md={6} lg={4} key={workflow.workflowID}>
                    <WorkflowCard
                      workflow={workflow}
                      onViewDetails={handleOpenDetailDialog}
                      onAssignTicket={handleOpenAssignDialog}
                      formatDate={formatDate}
                      getStatusColor={getStatusColor}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Paper>

      {/* Dialogs */}
      <CreateWorkflowDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        formData={newWorkflow}
        onFormChange={setNewWorkflow}
        onAddStep={handleAddStep}
        onRemoveStep={handleRemoveStep}
        onStepChange={handleStepChange}
        onCreate={handleCreateWorkflow}
        isCreating={creating}
      />

      <WorkflowDetailsDialog
        open={detailDialogOpen}
        onClose={() => { setDetailDialogOpen(false); setSelectedWorkflow(null); }}
        workflow={selectedWorkflow}
        getStatusColor={getStatusColor}
      />

      <AssignTicketDialog
        open={assignTicketDialogOpen}
        onClose={() => { setAssignTicketDialogOpen(false); setSelectedWorkflow(null); setSelectedTicketId(null); }}
        workflow={selectedWorkflow}
        availableTickets={availableTickets}
        selectedTicketId={selectedTicketId}
        onTicketSelect={setSelectedTicketId}
        onAssign={handleAssignTicket}
        isAssigning={assigning}
      />

      <CreateVorlageDialog
        open={createVorlageDialogOpen}
        onClose={() => setCreateVorlageDialogOpen(false)}
        formData={newVorlage}
        onFormChange={setNewVorlage}
        onAddStep={handleAddVorlageStep}
        onRemoveStep={handleRemoveVorlageStep}
        onStepChange={handleVorlageStepChange}
        onCreate={handleCreateVorlage}
        isCreating={creatingVorlage}
        availableUsers={availableUsers}
        availableWebhooks={availableWebhooks}
      />

      <ExecuteVorlageDialog
        open={executeVorlageDialogOpen}
        onClose={() => { setExecuteVorlageDialogOpen(false); setSelectedVorlage(null); setExecuteVorlageName(''); }}
        vorlage={selectedVorlage}
        workflowName={executeVorlageName}
        onWorkflowNameChange={setExecuteVorlageName}
        onExecute={handleExecuteVorlage}
        isExecuting={executingVorlage}
      />

      <EditVorlageDialog
        open={editVorlageDialogOpen}
        onClose={() => setEditVorlageDialogOpen(false)}
        formData={editVorlage}
        onFormChange={setEditVorlage}
        onAddStep={handleAddEditVorlageStep}
        onRemoveStep={handleRemoveEditVorlageStep}
        onStepChange={handleEditVorlageStepChange}
        onUpdate={handleUpdateVorlage}
        isUpdating={updatingVorlage}
        availableUsers={availableUsers}
        availableWebhooks={availableWebhooks}
      />
    </Box>
  );
};

export default Workflows;
